import { MainLayout } from '@components/layouts/main.layout';
import { BlueLabel } from '@components/shared/blue-label.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { NumberBadge } from '@components/shared/number-badge.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { useRequireAdmin } from '@hooks/use-require-admin.hook';
import { cn } from '@lib/utils';
import { useAdminLlmPromptsQuery } from '@queries/llm.query';
import { BracesIcon, FileTextIcon } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useMemo } from 'react';
import {
  buildPromptCardDescription,
  formatPromptCategoryLabel,
  formatPromptListTitle,
  groupPromptsByCategory,
} from './admin-llm-prompts.util';
import { AdminLlmPromptModal } from './components/admin-llm-prompt-modal.component';

export const AdminLlmPromptsPage: React.FC = () => {
  const { isAdmin, isPending: isAdminPending } = useRequireAdmin();
  const [selectedPromptKey, setSelectedPromptKey] = useQueryState('promptKey', parseAsString);
  const { data, isPending } = useAdminLlmPromptsQuery();

  const prompts = data?.templates ?? [];
  const selectedPrompt = useMemo(
    () => prompts.find((prompt) => prompt.key === selectedPromptKey),
    [prompts, selectedPromptKey],
  );
  const promptSections = useMemo(() => groupPromptsByCategory(prompts), [prompts]);

  if (isAdminPending || !isAdmin) {
    return null;
  }

  return (
    <PageviewTracker>
      <HelmetWrapper title="LLM Prompts" />
      <MainLayout
        asideClassName="hidden"
        contentClassName="w-full md:max-w-full"
      >
        <TooltipProvider>
          <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
            {isPending ? (
              <Tile isLoading />
            ) : prompts.length === 0 ? (
              <Tile
                emptyState={{
                  message: 'No prompt templates are registered yet.',
                  title: 'No Prompts Found',
                }}
                isEmpty
              />
            ) : (
              promptSections.map((section) => (
                <Tile
                  key={section.category}
                  header={{
                    title: (
                      <div className="flex items-center gap-1">
                        <NumberBadge value={section.prompts.length} />
                        <h3 className="text-base text-gray-700">{section.label}</h3>
                      </div>
                    ),
                  }}
                >
                  <section className="rounded-lg bg-gray-100 p-2">
                    <div className="flex flex-col gap-2">
                      {section.prompts.map((prompt) => {
                        const isSelected = selectedPromptKey === prompt.key;
                        const promptDescription = buildPromptCardDescription(prompt);

                        return (
                          <button
                            key={prompt.key}
                            className={cn(
                              'flex cursor-pointer flex-col rounded-xl border px-4 py-3.5 text-left transition-all',
                              'hover:border-blue-300 hover:bg-blue-50/30',
                              isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white',
                            )}
                            onClick={() => {
                              void setSelectedPromptKey(prompt.key);
                            }}
                            type="button"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex min-w-0 items-center gap-2">
                                <p className="truncate text-sm font-medium text-gray-800">
                                  {formatPromptListTitle(prompt)}
                                </p>

                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span>
                                      {prompt.isStructuredOutput ? (
                                        <BracesIcon className="size-3.5 text-blue-500" />
                                      ) : (
                                        <FileTextIcon className="size-3.5 text-gray-400" />
                                      )}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {prompt.isStructuredOutput ? 'Structured JSON output' : 'Text output'}
                                  </TooltipContent>
                                </Tooltip>
                              </div>

                              <Badge
                                size="sm"
                                variant="primary"
                              >
                                {`v${prompt.version}`}
                              </Badge>
                            </div>

                            {promptDescription && (
                              <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-gray-500">
                                {promptDescription}
                              </p>
                            )}

                            <div className={cn('grid grid-cols-2 gap-x-6', promptDescription ? 'mt-3' : 'mt-2')}>
                              <div>
                                <BlueLabel
                                  className="text-sm"
                                  text="Category"
                                />
                                <p className="mt-0.5 text-sm text-gray-700">
                                  {formatPromptCategoryLabel(prompt.category)}
                                </p>
                              </div>

                              <div>
                                <BlueLabel
                                  className="text-sm"
                                  text="Model"
                                />
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <p className="mt-0.5 truncate text-sm text-gray-700">{prompt.resolvedModel}</p>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {prompt.supportsDefaultModelOverride
                                      ? prompt.isOverridden && prompt.defaultModel !== prompt.codeDefaultModel
                                        ? `Backend fallback override from code default: ${prompt.codeDefaultModel}`
                                        : 'Backend fallback model'
                                      : 'This prompt usually uses the model selected in the consuming workflow.'}
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                </Tile>
              ))
            )}
          </div>
        </TooltipProvider>
      </MainLayout>

      {selectedPromptKey && (
        <AdminLlmPromptModal
          initialData={selectedPrompt}
          isOpen
          promptKey={selectedPromptKey}
          onClose={() => {
            void setSelectedPromptKey(null);
          }}
        />
      )}
    </PageviewTracker>
  );
};
